import React from 'react'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { EButtonType } from '@nextretreat/ui-components/dist/Button'
import theme from '@nextretreat/ui-components/dist/Theme'
import { rem, triangle } from 'polished'
import PropTypes from 'prop-types'
import { useMediaQuery } from 'QueryProvider'
import { BOXSHADOWS, COLORS, mq, radius, space } from 'Theme'
import sorterIcon from 'assets/images/svg/sorter.svg'
import xmarkIcon from 'assets/images/svg/xmark.svg'
import Button from 'components/atoms/Button'
import { Text } from 'components/atoms/Typography'
import OutsideClickHandler from 'components/OutsideClickHandler'
import Popover from 'components/Popover'
import { disableBodyScroll, enableBodyScroll } from 'utils/scroll'

const filterMQ = mq.to.tablet

const INVERSE_PLACEMENT_MAP = {
  top: 'bottom',
  bottom: 'top',
  left: 'right',
  right: 'left',
}

const ARROW_HEIGHT = rem('8px')
const CLOSE_BUTTON_SIZE = rem('18px')
export const PADDING = space.m

const arrowStyle = ({ placement }) => css`
  position: absolute;

  &[data-placement*='bottom'] {
    top: 0;
    left: 0;
  }

  &[data-placement*='top'] {
    bottom: 0;
    left: 0;
  }

  &[data-placement*='right'] {
    left: 0;
  }

  &[data-placement*='left'] {
    right: 0;
  }

  &::before,
  &::after {
    content: '';
    display: block;

    ${placement &&
    triangle({
      pointingDirection: INVERSE_PLACEMENT_MAP[placement],
      width: ['left', 'right'].includes(placement) ? ARROW_HEIGHT : rem('18px'),
      height: ['left', 'right'].includes(placement)
        ? rem('18px')
        : ARROW_HEIGHT,
      foregroundColor: theme.COLORS.WHITE,
    })}
  }

  &::before {
    z-index: -1;
    position: absolute;

    filter: drop-shadow(0px -2px 2px rgba(66, 149, 165, 0.25));
  }

  ${filterMQ`
    display: none;
  `}
`

const Card = styled('div')`
  width: min-content;
  min-width: ${({ customMinWidth }) => customMinWidth || rem('322px')};
  margin: ${ARROW_HEIGHT};

  background-color: ${theme.COLORS.WHITE};
  box-shadow: ${theme.BOXSHADOWS.LEVEL_1};
  border-radius: 8px;

  ${filterMQ`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    height: var(--nr-100vh, 80vh);
    width: auto;
    min-width: none;
    margin: 0;

    box-shadow: none;
    border-radius: none;
  `}
`

const Header = styled('div')`
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: ${PADDING};
  padding-bottom: ${space.s};
`

const Body = styled('div')`
  display: flex;
  flex-direction: column;

  min-height: 0;
`

const Content = styled('div')`
  overflow-y: auto;
  overflow-x: hidden;

  ${({ hasPadding }) =>
    hasPadding &&
    `
      padding: ${PADDING};
    `}
`

const Footer = styled('div')`
  position: relative;

  flex: none;
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: ${rem('56px')};
  padding: 0 1rem;

  background-color: ${COLORS.LYNX_WHITE};
  border-radius: 0 0 ${radius.m} ${radius.m};
  box-shadow: ${BOXSHADOWS.LIGHT};

  #popper[data-popper-reference-hidden] {
    visibility: hidden;
    pointer-events: none;
  }
`

const CloseButton = styled('button')`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  height: ${CLOSE_BUTTON_SIZE};
  width: ${CLOSE_BUTTON_SIZE};
`

const mobileOverrideStyle = {
  zIndex: 1,
  position: 'fixed',
  top: 0,
  right: 0,
  bottom: 0,
  left: 0,
}

const Filter = ({
  children,
  label,
  modalLabel = label,
  className,
  clearLabel,
  placement,
  isChanged,
  isOpen,
  onOpen,
  onClear,
  onCancel,
  dataCy,
  onApply,
  hasFooter,
  hasPadding,
  targetComp: TargetComp,
  targetCompProps,
  value,
  isMobile,
  isApplyDisabled,
  isDisabled,
  customMinWidth,
  isSorter = false,
  hasMargin = true,
  isInsideModal = false,
  zIndex,
}) => {
  const [isFilterOpen, setIsFilterOpen] = React.useState(isOpen)
  const contentRef = React.useRef(null)

  React.useEffect(() => {
    if (value) {
      setIsFilterOpen(false)
    }
  }, [value])

  React.useEffect(() => {
    if (isFilterOpen && isMobile) {
      disableBodyScroll(contentRef.current)
    } else if (contentRef.current) {
      enableBodyScroll(contentRef.current)
    }
  }, [isFilterOpen, isMobile])

  const popoverZIndex =
    zIndex || (isInsideModal ? 2 : mobileOverrideStyle.zIndex)

  return (
    <Popover
      targetComp={
        <TargetComp
          data-cy={dataCy}
          isActive={isFilterOpen}
          isOutlined={isChanged}
          disabled={isDisabled}
          style={{ marginRight: hasMargin ? '8px' : '0px' }}
          onClick={() => {
            if (!isDisabled) {
              if (onOpen) {
                onOpen()
              }
              setIsFilterOpen((prevIsOpen) => !prevIsOpen)
            }
          }}
          {...targetCompProps}
        >
          {isSorter && <img src={sorterIcon} width="15" alt="sorter" />}
          {label}
        </TargetComp>
      }
      arrowStyle={arrowStyle}
      placement={placement}
      isVisible={isFilterOpen}
      popoverStyle={
        isMobile
          ? {
              ...mobileOverrideStyle,
              zIndex: popoverZIndex,
            }
          : undefined
      }
      zIndex={popoverZIndex}
    >
      <OutsideClickHandler
        disabled={!isFilterOpen || isDisabled}
        onOutsideClick={() => {
          if (onCancel) {
            onCancel()
          }
          setIsFilterOpen(false)
        }}
      >
        <Card className={className} customMinWidth={customMinWidth}>
          <Body>
            {isMobile && (
              <Header>
                <Text fontSize="xl" fontWeight="semi_bold">
                  {modalLabel}
                </Text>
                <CloseButton
                  type="button"
                  onClick={() => {
                    if (onCancel) {
                      onCancel()
                    }
                    setIsFilterOpen(false)
                  }}
                >
                  <img src={xmarkIcon} alt="" />
                </CloseButton>
              </Header>
            )}
            <Content hasPadding={hasPadding} ref={contentRef}>
              {children}
            </Content>
          </Body>
          {hasFooter && (
            <Footer>
              <Button.Primary
                type="button"
                viewType={EButtonType.PLAIN}
                fontSize="s"
                onClick={() => {
                  if (onClear) {
                    onClear()
                  }
                }}
              >
                {clearLabel}
              </Button.Primary>
              <Button.Primary
                type="button"
                disabled={isApplyDisabled}
                onClick={() => {
                  if (onApply) {
                    onApply()
                  }
                  setIsFilterOpen(false)
                }}
              >
                Apply
              </Button.Primary>
            </Footer>
          )}
        </Card>
      </OutsideClickHandler>
    </Popover>
  )
}

Filter.defaultProps = {
  isOpen: false,
  hasFooter: true,
  hasPadding: true,
  targetComp: Button.Pill,
  clearLabel: 'Clear',
  isDisabled: false,
}

Filter.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  label: PropTypes.node.isRequired,
  modalLabel: PropTypes.node,
  clearLabel: PropTypes.string,
  placement: PropTypes.string,
  dataCy: PropTypes.string,
  isChanged: PropTypes.bool,
  isOpen: PropTypes.bool,
  onOpen: PropTypes.func,
  onClear: PropTypes.func,
  onCancel: PropTypes.func,
  onApply: PropTypes.func,
  hasFooter: PropTypes.bool,
  hasMargin: PropTypes.bool,
  isSorter: PropTypes.bool,
  hasPadding: PropTypes.bool,
  targetComp: PropTypes.object,
  targetCompProps: PropTypes.object,
  value: PropTypes.any,
  isMobile: PropTypes.bool,
  isApplyDisabled: PropTypes.bool,
  isDisabled: PropTypes.bool,
  customMinWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isInsideModal: PropTypes.bool,
  zIndex: PropTypes.number,
}

const ResponsiveFilter = (props) => {
  const matches = useMediaQuery()
  return <Filter isMobile={matches.mobile} {...props} />
}

export default ResponsiveFilter
